/* fx/FXImageSequence.js */

import { debounceRAF } from "../utils/timing";
import { lerp, clamp01 } from "../utils/math";
import { applyCSSVars } from "../utils/cssVars";

/**
 * FXImageSequence — Scroll-based canvas image sequence
 * ----------------------------------------------------
 * Maps scroll progress to a frame sequence rendered in a canvas.
 *
 * Example:
 *   new FXImageSequence({
 *     selector: ".main-header-animated",
 *     canvas: ".scroll-sequence",
 *     frameCount: 122,
 *     framePath: index => {
 *       const number = String(index + 1).padStart(4, "0");
 *       return `/neue/krafted/public/videos/frames/processors-${number}.webp`;
 *     },
 *     offsetStart: "0vh",
 *     offsetEnd: "50vh",
 *     cssVar: "--sequence-progress"
 *   });
 */

export class FXImageSequence {

    constructor(options = {}) {

        this.selector = options.selector;
        this.canvasSelector = options.canvas || "canvas";

        this.frameCount = options.frameCount || 0;
        this.framePath = options.framePath;

        this._exitAtOverride = options.exitAt ?? null;

        this.start = options.start ?? 0;
        this.end = options.end ?? 1;

        this.offsetStart = options.offsetStart ?? 0;
        this.offsetEnd = options.offsetEnd ?? 0;

        this.cssVar = options.cssVar || null;
        this.loadedVar = options.loadedVar || "--sequence-loaded";

        this.readyClass    = options.readyClass    || "is-sequence-ready";
        this.loadedClass   = options.loadedClass   || "is-sequence-loaded";
        this.completeClass = options.completeClass || "is-sequence-complete";

        /**
         * Number of frames needed before hiding the loader.
         * For a 122-frame sequence, 20–30 is a good starting point.
         */
        this.readyAfter = options.readyAfter ?? 24;

        this.onReady           = options.onReady           || null;
        this.onLoadedWanted    = options.onLoadedWanted    || null;
        this.onLoadedComplete  = options.onLoadedComplete  || null;
        this.onFirstFrameDraw  = options.onFirstFrameDraw  || null;
        this.onUnavailable     = options.onUnavailable     || null;
        this.onUpdate          = options.onUpdate          || null;
        this.onLoadProgress    = options.onLoadProgress    || null;

        /**
         * Minimum time (ms) the loading progress takes to reach 1.0,
         * regardless of how fast images actually load.
         * displayedProgress = min(actualProgress, elapsed / minLoadDuration)
         */
        this.minLoadDuration = options.minLoadDuration ?? 0;
        this._displayLoopActive = false;

        /**
         * Number of frames that must be loaded before the loader lifts.
         * Converted to exitAt in init() once frameCount is resolved.
         * Ignored if exitAt is explicitly provided.
         */
        this.priorityCount = options.priorityCount ?? 40;

        /**
         * Smoothing time constant in seconds.
         * 0 = direct (no smoothing), 0.1 = snappy, 0.3 = cinematic.
         */
        this.tau         = options.tau         ?? 0;
        this.minDelta    = options.minDelta    ?? 0.001;
        this.concurrency = options.concurrency ?? 6;

        this.isRendering = false;
        this.lastTime    = 0;

        this.items = [...document.querySelectorAll(this.selector)]
            .map(section => {

                const canvas = section.querySelector(this.canvasSelector);
                const ctx = canvas ? canvas.getContext("2d") : null;

                return {
                    section,
                    canvas,
                    ctx,

                    width: 0,
                    height: 0,
                    dpr: 1,

                    images: [],
                    loaded: 0,
                    failed: 0,
                    processed: 0,
                    loadStartTime: 0,

                    currentFrame: -1,
                    requestedFrame: 0,

                    targetProgress:  0,
                    currentProgress: 0,

                    hasPainted: false,
                    isReady: false,
                    isLoaded: false,
                    isComplete: false
                };

            })
            .filter(item => item.canvas && item.ctx);


        this.requestUpdate = debounceRAF(() => {
            this.update();
        });

        this.requestResize = debounceRAF(() => {
            this.resizeAll();
            this.update();
        });

        this.requestRender = debounceRAF(() => {
            this.render();
        });

        this.onScroll = this.onScroll.bind(this);
        this.onResize = this.onResize.bind(this);

        this.init();

    }

    init() {

        if (!this.frameCount) {
            const first = this.items[0];
            if (first) {
                const count = parseInt(first.section.dataset.frameCount, 10);
                if (count > 0) this.frameCount = count;
            }
        }

        if (!this.frameCount || !this.framePath) {
            if (this.onUnavailable) {
                this.onUnavailable();
            }
            return;
        }

        this.exitAt = this._exitAtOverride !== null
            ? Math.min(Math.max(this._exitAtOverride, 0.01), 1)
            : clamp01(this.priorityCount / this.frameCount);

        this.items.forEach(item => {

            this.resizeItem(item);
            this.preload(item);

            item.targetProgress  = this.getProgress(item.section);
            item.currentProgress = item.targetProgress;

        });

        window.addEventListener("scroll", this.onScroll, { passive: true });
        window.addEventListener("resize", this.onResize);

        this.startRender();

    }

    preload(item) {

        item.loadStartTime = performance.now();

        if (this.minLoadDuration > 0) {
            this._startDisplayLoop();
        }

        let cursor = 0;
        let active = 0;

        const next = () => {

            while (active < this.concurrency && cursor < this.frameCount) {

                const index = cursor++;
                active++;

                const img = new Image();
                img.decoding = "async";
                img.src = this.framePath(index);

                img.decode()
                    .then(() => {

                        item.images[index] = img;
                        item.loaded++;
                        item.processed++;
                        this.updateLoadedState(item);

                        if (item.currentFrame === -1) {
                            this.draw(item, 0);
                        }

                        this.draw(item, item.requestedFrame);

                    })
                    .catch(() => {

                        item.failed++;
                        item.processed++;
                        this.updateLoadedState(item);

                    })
                    .finally(() => {

                        active--;
                        next();

                    });

            }

        };

        next();

    }

    updateLoadedState(item) {

        if (this.minLoadDuration <= 0) {
            const actual = clamp01(item.loaded / this.frameCount);
            this._applyDisplayProgress(item, clamp01(actual / this.exitAt));
        }
        // When minLoadDuration > 0, tickDisplay drives the display independently.

        if (!item.isComplete && item.processed >= this.frameCount) {

            item.isComplete = true;
            item.section.classList.add(this.completeClass);

            if (this.onLoadedComplete) {
                this.onLoadedComplete(item.section, item.canvas);
            }

        }

    }

    _startDisplayLoop() {

        if (this._displayLoopActive) return;

        this._displayLoopActive = true;

        const tick = () => {

            if (!this._displayLoopActive) return;

            let allDone = true;

            this.items.forEach(item => {

                if (item.isLoaded) return;

                allDone = false;

                const actualProgress  = clamp01(item.loaded / this.frameCount);
                const scaledProgress  = clamp01(actualProgress / this.exitAt);
                const elapsed         = performance.now() - item.loadStartTime;
                const timeProgress    = clamp01(elapsed / this.minLoadDuration);
                const displayedProgress = Math.min(scaledProgress, timeProgress);

                this._applyDisplayProgress(item, displayedProgress);

            });

            if (!allDone) requestAnimationFrame(tick);

        };

        requestAnimationFrame(tick);

    }

    _applyDisplayProgress(item, displayedProgress) {

        applyCSSVars(item.section, {
            [this.loadedVar]: displayedProgress.toFixed(4)
        });

        if (this.onLoadProgress) {
            const displayedLoaded = Math.round(displayedProgress * this.frameCount);
            this.onLoadProgress(item.section, item.canvas, displayedProgress, displayedLoaded, this.frameCount);
        }

        const displayedLoaded = Math.round(displayedProgress * this.frameCount);

        if (!item.isReady && displayedLoaded >= this.readyAfter) {

            item.isReady = true;
            item.section.classList.add(this.readyClass);

            if (this.onReady) {
                this.onReady(item.section, item.canvas);
            }

        }

        if (!item.isLoaded && displayedProgress >= 1) {

            item.isLoaded = true;
            item.section.classList.add(this.loadedClass);

            if (this.onLoadedWanted) {
                this.onLoadedWanted(item.section, item.canvas);
            }

        }

    }

    onScroll() {

        this.requestUpdate();

    }

    onResize() {

        this.requestResize();

    }

    resizeAll() {

        this.items.forEach(item => {

            this.resizeItem(item);

            if (item.currentFrame >= 0) {
                this.draw(item, item.currentFrame, true);
            }

        });

    }

    resizeItem(item) {

        const { canvas, ctx } = item;

        const rect = canvas.getBoundingClientRect();

        const dpr = window.devicePixelRatio || 1;

        item.width = rect.width;
        item.height = rect.height;
        item.dpr = dpr;

        canvas.width = Math.round(rect.width * dpr);
        canvas.height = Math.round(rect.height * dpr);

        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    }

    update() {

        this.items.forEach(item => {
            item.targetProgress = this.getProgress(item.section);
        });

        this.startRender();

    }

    startRender() {

        if (this.isRendering) return;

        this.isRendering = true;
        this.lastTime    = performance.now();

        // tau=0: no lerp needed — render synchronously in the current rAF instead of
        // scheduling a second rAF cycle (saves ~16ms of input latency on every scroll).
        if (this.tau <= 0) {
            this.render();
        } else {
            this.requestRender();
        }

    }

    render() {

        const now = performance.now();
        const dt  = Math.max((now - this.lastTime) / 1000, 0.001);

        this.lastTime = now;

        let shouldContinue = false;

        this.items.forEach(item => {

            const diff = item.targetProgress - item.currentProgress;

            if (this.tau <= 0) {

                item.currentProgress = item.targetProgress;

            } else if (Math.abs(diff) > this.minDelta) {

                const alpha = 1 - Math.exp(-dt / this.tau);

                item.currentProgress += diff * alpha;
                shouldContinue = true;

            } else {

                item.currentProgress = item.targetProgress;

            }

            item.currentProgress = clamp01(item.currentProgress);

            const mappedProgress = lerp(this.start, this.end, item.currentProgress);

            const frame = Math.round(
                clamp01(mappedProgress) * (this.frameCount - 1)
            );

            item.requestedFrame = frame;

            if (frame !== item.currentFrame) {
                this.draw(item, frame);
            }

            if (this.cssVar) {
                applyCSSVars(item.section, {
                    [this.cssVar]: item.currentProgress.toFixed(4)
                });
            }

            if (this.onUpdate) {
                this.onUpdate(item.section, item.canvas, item.currentProgress, frame);
            }

        });

        if (shouldContinue) {
            this.requestRender();
        } else {
            this.isRendering = false;
        }

    }

    draw(item, frame, force = false) {

        const image = this.getAvailableImage(item, frame);

        if (!image) return;

        if (!force && frame === item.currentFrame) return;

        item.currentFrame = frame;

        const { ctx, width, height } = item;

        const iw = image.naturalWidth  ?? image.width;
        const ih = image.naturalHeight ?? image.height;

        if (!iw || !ih || !width || !height) return;

        /**
         * object-fit: cover logic
         */
        const scale = Math.max(
            width / iw,
            height / ih
        );

        const drawWidth = iw * scale;
        const drawHeight = ih * scale;

        const x = (width - drawWidth) / 2;
        const y = (height - drawHeight) / 2;

        ctx.clearRect(0, 0, width, height);
        ctx.drawImage(
            image,
            x,
            y,
            drawWidth,
            drawHeight
        );

        if (!item.hasPainted) {
            item.hasPainted = true;

            if (this.onFirstFrameDraw) {
                this.onFirstFrameDraw(item.section, item.canvas, frame, image);
            }
        }

    }

    getAvailableImage(item, frame) {

        const exact = item.images[frame];

        if (exact) {
            return exact;
        }

        /**
         * Fallback: use nearest loaded previous frame.
         */
        for (let i = frame - 1; i >= 0; i--) {

            if (item.images[i]) return item.images[i];

        }

        /**
         * Fallback: use nearest loaded next frame.
         */
        for (let i = frame + 1; i < this.frameCount; i++) {

            if (item.images[i]) return item.images[i];

        }

        return null;

    }

    getProgress(section) {

        const rect = section.getBoundingClientRect();

        const totalScrollableDistance = section.offsetHeight - window.innerHeight;

        if (totalScrollableDistance <= 0) return 0;

        const startOffset = this.parseOffset(
            this.offsetStart,
            totalScrollableDistance
        );

        const endOffset = this.parseOffset(
            this.offsetEnd,
            totalScrollableDistance
        );

        const animationStart = startOffset;
        const animationEnd = totalScrollableDistance - endOffset;
        const animationDistance = animationEnd - animationStart;

        if (animationDistance <= 0) return 1;

        const scrollInsideSection = -rect.top;

        return clamp01(
            (scrollInsideSection - animationStart) / animationDistance
        );

    }

    parseOffset(value, reference = window.innerHeight) {

        if (typeof value === "number") {
            return value;
        }

        if (typeof value !== "string") {
            return 0;
        }

        const cleanValue = value.trim();
        const number = parseFloat(cleanValue);

        if (Number.isNaN(number)) {
            return 0;
        }

        if (cleanValue.endsWith("px")) {
            return number;
        }

        if (
            cleanValue.endsWith("vh") ||
            cleanValue.endsWith("svh") ||
            cleanValue.endsWith("dvh")
        ) {
            return window.innerHeight * number / 100;
        }

        if (cleanValue.endsWith("%")) {
            return reference * number / 100;
        }

        return number;

    }

    destroy() {

        this._displayLoopActive = false;

        window.removeEventListener("scroll", this.onScroll);
        window.removeEventListener("resize", this.onResize);

        this.items.forEach(item => {
            item.images = [];
        });

    }

}
